/**
 * TeachBud Assessment Engine
 *
 * Types for the AI-generated assessment system.
 * Assessments are derived from the lesson — every question traces back to a specific
 * lesson section. The AI is constrained to only assess skills taught in the lesson.
 */

// ── Question types ──────────────────────────────────────────────────────────

export type QuestionKind =
  | 'multiple-choice'
  | 'short-answer'
  | 'constructed-response'
  | 'true-false'
  | 'fill-in-the-blank'
  | 'matching'
  | 'performance-task';

export type BloomsLevel =
  | 'Remember'
  | 'Understand'
  | 'Apply'
  | 'Analyze'
  | 'Evaluate'
  | 'Create';

export type AssessmentDifficulty = 'easier' | 'same' | 'harder';

export type AssessmentLayout =
  | 'student-friendly'
  | 'testing-format'
  | 'state-test-style'
  | 'worksheet-style';

export type AssessmentVersion = 'A' | 'B' | 'C';

// ── Configuration ────────────────────────────────────────────────────────────

export interface AssessmentConfig {
  questionCount: 5 | 10 | 15 | 20;
  difficulty: AssessmentDifficulty;
  questionTypes: QuestionKind[];
  bloomsLevel: BloomsLevel;
  version: AssessmentVersion;
  includeAnswerKey: boolean;
  separateTeacherCopy: boolean;
  shuffleQuestions: boolean;
  shuffleChoices: boolean;
  graphics: 'none' | 'minimal' | 'standard' | 'rich';
  layout: AssessmentLayout;
  showNameLine: boolean;
  showDateLine: boolean;
  showTeacherLine: boolean;
  showPeriodLine: boolean;
}

export function defaultAssessmentConfig(): AssessmentConfig {
  return {
    questionCount: 10,
    difficulty: 'same',
    questionTypes: ['multiple-choice', 'short-answer'],
    bloomsLevel: 'Apply',
    version: 'A',
    includeAnswerKey: true,
    separateTeacherCopy: true,
    shuffleQuestions: false,
    shuffleChoices: false,
    graphics: 'minimal',
    layout: 'student-friendly',
    showNameLine: true,
    showDateLine: true,
    showTeacherLine: false,
    showPeriodLine: false,
  };
}

// ── Question data ─────────────────────────────────────────────────────────────

export interface AssessmentQuestion {
  id: string;
  kind: QuestionKind;
  stem: string;
  /** Multiple choice and True/False options */
  choices?: string[];
  /** Matching pairs (left = term, right = definition/answer) */
  matchingPairs?: { left: string; right: string }[];
  /** Correct answer — string for MC letter, text for others */
  answer: string;
  /** Brief explanation of the correct answer */
  explanation: string;
  /** Most common student error on this type of problem */
  commonMistake?: string;
  /** Brief instructional note for the teacher */
  teacherTip?: string;
  /** Which lesson section this question derives from */
  lessonSection: string;
  bloomsLevel: BloomsLevel;
  difficulty: AssessmentDifficulty;
  points: number;
}

// ── Generated assessment ──────────────────────────────────────────────────────

export interface GeneratedAssessment {
  title: string;
  grade: string;
  gradeLabel: string;
  subject: string;
  standard: string;
  skillToTeach: string;
  version: AssessmentVersion;
  config: AssessmentConfig;
  questions: AssessmentQuestion[];
  totalPoints: number;
  generatedAt: string;
  /**
   * Snapshot of the lesson's content key at the time of generation.
   * Used to detect when the source lesson has changed since the assessment
   * was generated, triggering a sync-out-of-date warning in the UI.
   */
  syncKey?: string;
  /** School / district branding to render in the printed header. */
  branding?: Record<string, unknown>;
}
